/**
 * Register a person from photographs — the registry drawer's own way in, as a dialog.
 *
 * ─── WHY IT LEFT THE DRAWER ───
 * These fields used to be a card that unfolded INSIDE the Registered People panel, above the
 * list. That put a form with four inputs, a file picker and the watchlist block into a 28rem
 * column that was already scrolling a hundred cards, so the Register button and the field it
 * belonged to were routinely on different screens — and the list kept moving underneath as the
 * form grew and shrank. Worse, opening the form did not stop the operator scrolling away from
 * it; a half-typed registration would simply be somewhere above the viewport.
 *
 * A dialog is what the act actually is: a decision with its own beginning and end, taken
 * against the registry rather than within it. It is the same shell as `PersonBlacklistDialog`
 * next door, which is the point — registering somebody and listing somebody are the two things
 * this drawer does, and they should not be two different kinds of interaction.
 *
 * ─── IT OWNS ITS FIELDS ───
 * Like the watchlist dialog, and for the same reason: the two dashboards were each carrying
 * their own copy of this form, field for field, and a form kept in the page's state is a form
 * that has to be reset by hand in three places. The caller passes a submit function and gets
 * back what the operator typed.
 *
 * ─── A HALF-DONE REGISTRATION IS NOT A FAILURE ───
 * Enrolling the person and putting them on the watchlist are two calls (see the caller), and
 * the second can fail after the first has succeeded. That is NOT a rejection — the person
 * exists, their face is enrolled, and offering "Register" again would enrol them twice. So a
 * resolved `{ warning }` is a third outcome: the message is shown, the form is sealed, and the
 * only button left is Close.
 */
import { useEffect, useState } from 'react';
import { ExclamationCircleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Field, WATCHLIST_INPUT } from '../common/watchlistForm';
import { PersonWatchlistToggle, type PersonWatchlistValue } from './PersonWatchlistForm';

/**
 * What kind of visitor somebody is — NOT whether they are flagged.
 *
 * "Employee" is deliberately absent: employment is a record attached to a person, not a kind
 * of person, and putting it here would make it a choice that could only be made once. So is
 * "blacklist", which some registries still hold as a label and which never did anything —
 * alerting reads the watchlist columns, not this field. See `PersonWatchlistForm`.
 */
export const PERSON_LABELS: Array<{ value: string; label: string }> = [
  { value: 'visitor', label: 'Visitor' },
  { value: 'vip', label: 'VIP' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

export const MAX_REGISTER_PHOTOS = 5;

export interface PersonRegisterValue {
  name: string;
  label: string;
  notes: string;
  files: File[];
  /** The watchlist entry to create alongside the person, or null for none. */
  watchlist: PersonWatchlistValue | null;
}

export interface PersonRegisterDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Register. Rejecting shows the message and keeps the form editable; resolving with
   * `{ warning }` means the person WAS created but something after it was not — the message
   * is shown and the form is sealed, because retrying would register them twice.
   */
  onSubmit: (value: PersonRegisterValue) => Promise<void | { warning?: string | null }>;
  /** Offer the watchlist opt-in. The caller gates this on the watchlist permission. */
  canManageWatchlist?: boolean;
}

export default function PersonRegisterDialog({
  open, onClose, onSubmit, canManageWatchlist,
}: PersonRegisterDialogProps) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('visitor');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [watchlist, setWatchlist] = useState<PersonWatchlistValue | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set by a resolved warning: the person exists, so there is nothing left to submit. */
  const [warning, setWarning] = useState<string | null>(null);

  // Cleared on OPEN rather than on close, so the fields are never seen emptying themselves as
  // the dialog animates away, and a second registration never starts on the first one's name.
  useEffect(() => {
    if (!open) return;
    setName('');
    setLabel('visitor');
    setNotes('');
    setFiles([]);
    setWatchlist(null);
    setError(null);
    setWarning(null);
    setBusy(false);
  }, [open]);

  if (!open) return null;

  const ready = !!name.trim() && files.length > 0;

  async function submit() {
    if (!ready || busy || warning) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onSubmit({ name: name.trim(), label, notes, files, watchlist });
      if (result && result.warning) setWarning(result.warning);
      else onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not register this person');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      // `md`, not `sm`: the watchlist block below carries a three-up category row that wraps
      // mid-word in a 28rem column.
      size="md"
      ariaLabel="Register a person"
      title={
        <span className="flex items-center gap-2">
          <UserPlusIcon className="h-5 w-5 shrink-0 text-brand" />
          Register a person
        </span>
      }
      subtitle="From photographs — the other way in is a face on the report behind this panel."
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border border-border px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {warning ? 'Close' : 'Cancel'}
          </button>
          {/* Gone once the person exists — see the header comment. */}
          {!warning && (
            <button
              type="button"
              disabled={busy || !ready}
              onClick={submit}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              )}
              {busy ? 'Registering…' : 'Register'}
            </button>
          )}
        </div>
      }
    >
      {/* The Modal shell is padding-free by design — content owns its inset. */}
      <div className="space-y-4 px-5 py-4">
        <Field label="Name *">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
            placeholder="Enter person's name"
            autoFocus
            disabled={busy || !!warning}
            className={`${WATCHLIST_INPUT} disabled:opacity-50`}
          />
        </Field>

        <Field label="Type" hint="What kind of visitor they are. It does not flag anybody.">
          <select
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy || !!warning}
            className={`${WATCHLIST_INPUT} disabled:opacity-50`}
          >
            {PERSON_LABELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </Field>

        <Field label="Notes">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes…"
            disabled={busy || !!warning}
            className={`${WATCHLIST_INPUT} disabled:opacity-50`}
          />
        </Field>

        <Field
          label={`Photos * (up to ${MAX_REGISTER_PHOTOS})`}
          hint={'Several angles enrol better than one. The AI module embeds them in the '
            + 'background, so the badge reads “enrolment pending” for a moment.'}
        >
          <label className={`flex items-center justify-center rounded-lg border border-dashed border-border px-3 py-3 text-[12px] transition-colors ${
            busy || warning
              ? 'cursor-not-allowed opacity-50 text-muted-foreground'
              : 'cursor-pointer text-muted-foreground hover:border-brand hover:text-foreground'
          }`}>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              disabled={busy || !!warning}
              onChange={(e) => {
                setFiles(e.target.files ? Array.from(e.target.files).slice(0, MAX_REGISTER_PHOTOS) : []);
                setError(null);
              }}
            />
            {files.length
              ? `${files.length} photo${files.length > 1 ? 's' : ''} selected`
              : 'Choose photos'}
          </label>
        </Field>

        {/* The same opt-in the event-registration modal carries: somebody enrolled from an HR
            photo can be a barred ex-contractor just as easily as somebody first seen on
            camera, and the two ways in should not disagree about what can be decided at
            registration. */}
        {canManageWatchlist && (
          <PersonWatchlistToggle
            value={watchlist}
            onChange={setWatchlist}
            name={name}
            disabled={busy || !!warning}
          />
        )}

        {warning && (
          <p className="flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-[12px] leading-snug text-amber-500">
            <ExclamationCircleIcon className="mt-px h-4 w-4 shrink-0" />
            <span className="min-w-0">{warning}</span>
          </p>
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-[12px] leading-snug text-red-500">
            <ExclamationCircleIcon className="mt-px h-4 w-4 shrink-0" />
            <span className="min-w-0">{error}</span>
          </p>
        )}
      </div>
    </Modal>
  );
}
